<template>
    <div :class="`spinner spinner-align-${localAlign}`">
        <label for="spinner-input" @click="focusFieldLabel" v-if="!editingFieldLabel">{{ displayFieldLabel }}</label>
        <input
            v-if="editingFieldLabel"
            type="text"
            class="field-label-input"
            placeholder="Bir sayı girin"
            v-model="localFieldLabel"
            @blur="stopEditingFieldLabel"
            @keydown.enter="stopEditingFieldLabel"
            ref="fieldLabelInput"
        />
        <div class="input-wrapper">
            <div class="spinner-wrapper">
                <button @click="decrement" class="spinner-button decrement" type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </button>
                <input
                    type="number"
                    id="spinner-input"
                    v-model="spinnerValue"
                    min="0"
                    @input="updateField"
                    @focus="onFocus"
                    @blur="onBlur"
                />
                <button @click="increment" class="spinner-button increment" type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "FormSpinner",
    props: {
        fieldLabel: String,
        align: String,
        item: Object,
        isRequired: Boolean,
    },
    data() {
        return {
            localFieldLabel: this.fieldLabel || "",
            localAlign: this.align || "top",
            spinnerValue: 0,
            editingFieldLabel: false,
            updateTimer: null,
        };
    },
    computed: {
        displayFieldLabel() {
            return this.localFieldLabel || "Bir sayı girin";
        },
    },
    methods: {
        ...mapActions("forms", ["updateFormField"]),
        focusFieldLabel() {
            this.editingFieldLabel = true;
            this.$nextTick(() => {
                if (this.$refs.fieldLabelInput) {
                    this.$refs.fieldLabelInput.focus();
                }
            });
        },
        stopEditingFieldLabel() {
            this.editingFieldLabel = false;
            this.updateField();
        },
        onFocus() {
            // Focus handling
        },
        onBlur() {
            // Blur handling
        },
        increment() {
            this.spinnerValue++;
            this.updateField();
        },
        decrement() {
            if (this.spinnerValue > 0) {
                this.spinnerValue--;
                this.updateField();
            }
        },
        updateField() {
            let self = this;
            if (this.updateTimer) {
                clearTimeout(this.updateTimer);
            }
            this.updateTimer = setTimeout(function () {
                const updateFields = {
                    spinner: {
                        fieldLabel: self.localFieldLabel,
                        value: self.spinnerValue,
                        align: self.localAlign,
                        isRequired: self.isRequired,
                    },
                };
                self.updateFormField({
                    siteId: self.item.siteId,
                    formFieldId: self.item._id,
                    updateFields: updateFields,
                });
                self.$emit("update-field", updateFields);
            }, 200);
        },
    },
    watch: {
        fieldLabel(newVal) {
            this.localFieldLabel = newVal;
        },
        align(newVal) {
            this.localAlign = newVal;
        },
        "item.value"(newVal) {
            this.spinnerValue = newVal;
        },
        'item.spinner.fieldLabel': function (v) { this.localFieldLabel = v; },
        'item.spinner.value': function (v) { this.spinnerValue = v; },
    },
};
</script>

<style lang="scss" scoped>
.spinner {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    background: linear-gradient(145deg,
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s ease;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

    &:hover {
        background: linear-gradient(145deg,
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    > label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin: 0 0 clamp(0.5rem, 1.2vw, 0.75rem) 0;
        color: rgba(55, 65, 81, 0.9);
        line-height: 1.4;
        cursor: pointer;
    }

    .field-label-input {
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        color: rgba(55, 65, 81, 0.9);
        line-height: 1.4;
        border: none;
        background: transparent;
        outline: none;
        border-bottom: 2px solid #f26a52;
        padding: 0 0 2px 0;

        &::placeholder {
            color: rgba(156, 163, 175, 0.7);
        }
    }

    .input-wrapper {
        position: relative;
    }

    .spinner-wrapper {
        display: flex;
        align-items: center;
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(8px);
        border: 2px solid rgba(209, 213, 219, 0.6);
        border-radius: clamp(0.5rem, 1.5vw, 0.75rem);
        overflow: hidden;
        transition: all 0.3s ease;
        position: relative;

        &:hover {
            border-color: #f26a52;
            box-shadow: 0 4px 12px rgba(242, 106, 82, 0.15);
        }

        &:focus-within {
            border-color: #f26a52;
            box-shadow: 0 0 0 3px rgba(242, 106, 82, 0.1);
        }
    }

    input[type="number"] {
        flex: 1;
        padding: clamp(0.75rem, 2vw, 1rem) clamp(0.5rem, 1.5vw, 0.75rem);
        font-size: clamp(0.875rem, 2vw, 1rem);
        font-weight: 500;
        color: rgba(55, 65, 81, 0.9);
        background: transparent;
        border: none;
        text-align: center;
        outline: none;
        font-family: inherit;
        min-width: clamp(3.5rem, 8vw, 4rem);

        &::placeholder {
            color: rgba(107, 114, 128, 0.7);
            font-weight: 400;
        }

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        &[type=number] {
            appearance: textfield;
            -moz-appearance: textfield;
        }
    }

    .spinner-button {
        width: clamp(2.75rem, 6vw, 3rem);
        height: clamp(2.75rem, 6vw, 3rem);
        background: rgba(255, 255, 255, 0.6);
        border: none;
        color: rgba(107, 114, 128, 0.7);
        cursor: pointer;
        outline: none;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        position: relative;
        flex-shrink: 0;

        svg {
            width: clamp(1rem, 2.2vw, 1.125rem);
            height: clamp(1rem, 2.2vw, 1.125rem);
            transition: all 0.3s ease;
        }

        &:hover {
            background: rgba(242, 106, 82, 0.1);
            color: #f26a52;
            transform: scale(1.05);

            svg {
                transform: scale(1.1);
            }
        }

        &:active {
            transform: scale(0.95);
            background: #f26a52;
            color: white;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;

            &:hover {
                background: rgba(255, 255, 255, 0.6);
                color: rgba(107, 114, 128, 0.7);
                transform: none;
            }
        }
    }

    .decrement {
        border-right: 1px solid rgba(209, 213, 219, 0.6);

        &:hover {
            border-right-color: transparent;
        }
    }

    .increment {
        border-left: 1px solid rgba(209, 213, 219, 0.6);

        &:hover {
            border-left-color: transparent;
        }
    }
}

/* Alignment Classes */
.spinner-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label, .field-label-input {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: left;
    }

    .input-wrapper {
        flex: 1;
        min-width: 0;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;

        > label, .field-label-input {
            width: 100%;
            text-align: left;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }

        .input-wrapper {
            width: 100%;
        }
    }
}

.spinner-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label, .field-label-input {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: right;
    }

    .input-wrapper {
        flex: 1;
        min-width: 0;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;

        > label, .field-label-input {
            width: 100%;
            text-align: left;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }

        .input-wrapper {
            width: 100%;
        }
    }
}

.spinner-align-top {
    > label {
        text-align: left;
    }
}

@media (max-width: 768px) {
    .spinner {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);

        .spinner-wrapper {
            border-radius: clamp(0.375rem, 1vw, 0.5rem);
        }

        input[type="number"] {
            padding: clamp(0.625rem, 1.5vw, 0.75rem) clamp(0.375rem, 1vw, 0.5rem);
            font-size: clamp(0.8rem, 1.8vw, 0.875rem);
            min-width: clamp(3rem, 6vw, 3.5rem);
        }

        .spinner-button {
            width: clamp(2.5rem, 5vw, 2.75rem);
            height: clamp(2.5rem, 5vw, 2.75rem);

            svg {
                width: clamp(0.875rem, 2vw, 1rem);
                height: clamp(0.875rem, 2vw, 1rem);
            }
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .spinner, .spinner-wrapper, .spinner-button {
        transition: none;
    }

    .spinner-button:hover, .spinner-button:active {
        transform: none;
    }
}
</style>
